import React, { useRef } from 'react'
import QRCode from 'react-qr-code'
import { jsPDF } from 'jspdf'
import QRCodeGenerator from 'qrcode'
import { useTranslation } from 'react-i18next'
import ModalComponent from 'components/common/ModalComponent'
import ButtonComponent from 'components/common/ButtonComponent'
import 'styles/QRModal.css'
import { QR_TYPES } from 'constants/qr.types'

// Legacy worker code understood by gf-app: 30392000000000 with the oldId
// overlaid on the right (e.g. oldId 5510 → 30392000005510). A 14-digit numeric
// payload fits in a version-1 QR (21×21 modules), so it stays large and easy to
// scan on a small sticker. The app resolves it against its worker catalog.
const LEGACY_WORKER_PREFIX = '30392000000000'

const getWorkerQrValue = (worker) => {
  const oldId = String(worker.oldId ?? '').trim()
  if (/^\d{1,9}$/.test(oldId)) {
    return LEGACY_WORKER_PREFIX.slice(0, LEGACY_WORKER_PREFIX.length - oldId.length) + oldId
  }
  // No numeric oldId: fall back to JSON, keeping only what the app reads.
  return JSON.stringify({
    qrType: QR_TYPES.WORKER,
    id: worker.workerId,
    name: worker.firstName,
    lastName: worker.lastName,
    oldId: worker.oldId
  })
}

const QRModal = ({ isOpen, onClose, worker }) => {
  const { t } = useTranslation()
  const qrRef = useRef(null)

  const handlePrintQR = async () => {
    try {
      const qrDataUrl = await QRCodeGenerator.toDataURL(getWorkerQrValue(worker), {
        errorCorrectionLevel: 'M',
        margin: 0,
        width: 512,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      })

      // Sticker: 40 × 50 mm portrait
      const PAGE_WIDTH = 40
      const PAGE_HEIGHT = 50
      const MARGIN = 2.5
      const PT_TO_MM = 0.3528
      const LINE_HEIGHT = 1.1

      // eslint-disable-next-line new-cap
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: [PAGE_WIDTH, PAGE_HEIGHT]
      })

      const fullName = `${worker.firstName} ${worker.lastName}`
      const workerId = String(worker.oldId || worker.workerId)

      // Header: worker ID top-right, name top-left wrapped to at most 3 lines
      const ID_FONT = 12
      pdf.setFont('helvetica', 'bold')
      pdf.setTextColor(0, 0, 0)
      pdf.setFontSize(ID_FONT)
      const idWidth = pdf.getTextWidth(workerId)
      pdf.text(workerId, PAGE_WIDTH - MARGIN, MARGIN + ID_FONT * PT_TO_MM, { align: 'right' })

      const HEADER_H = 12
      const nameWidth = PAGE_WIDTH - 2 * MARGIN - idWidth - 1.5
      let nameFont = 10
      let nameLines
      for (; nameFont >= 5; nameFont -= 0.5) {
        pdf.setFontSize(nameFont)
        nameLines = pdf.splitTextToSize(fullName.toUpperCase(), nameWidth)
        if (nameLines.length * nameFont * PT_TO_MM * LINE_HEIGHT <= HEADER_H) break
      }
      pdf.setFont('helvetica', 'normal')
      pdf.text(nameLines.slice(0, 3), MARGIN, MARGIN + nameFont * PT_TO_MM, { lineHeightFactor: LINE_HEIGHT })

      // QR fills the rest of the sticker, with a white quiet zone around it
      const qrTop = MARGIN + HEADER_H + 1
      const qrSize = Math.min(PAGE_WIDTH - 2 * (MARGIN + 1), PAGE_HEIGHT - qrTop - MARGIN - 1)
      const qrX = (PAGE_WIDTH - qrSize) / 2
      pdf.addImage(qrDataUrl, 'PNG', qrX, qrTop + 1, qrSize, qrSize)

      // Filename: worker_Juan-Garcia_W001_2026-08-13.pdf
      const slug = s => (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, '-').replace(/[^a-zA-Z0-9-]/g, '')
      const date = new Date(worker.updatedAt || worker.createdAt).toISOString().slice(0, 10)
      const filename = `worker_${slug(fullName)}_${slug(workerId)}_${date}.pdf`
      pdf.save(filename)
    } catch (err) {
      console.error('Error generating PDF:', err)
    }
  }

  if (!worker) return null

  const qrValue = getWorkerQrValue(worker)

  const customFooter = (
    <>
      <ButtonComponent onClick={handlePrintQR} className='bg-green-600 text-white hover:bg-green-700'>
        {t('mgmt.qrModal.printQr')}
      </ButtonComponent>
      <ButtonComponent onClick={onClose} customColorsStyles='bg-gray-100 text-greenPrimary hover:text-white'>
        {t('mgmt.common.close')}
      </ButtonComponent>
    </>
  )

  return (
    <ModalComponent
      isOpen={isOpen}
      onClose={onClose}
      title={t('mgmt.qrModal.title')}
      customFooter={customFooter}
    >
      <div className='qr-content'>
        <div className='worker-info mb-4'>
          <h4 className='text-lg font-semibold'>{worker.firstName} {worker.lastName}</h4>
          <p className='text-gray-600'>ID: {worker.oldId || worker.workerId}</p>
        </div>

        <div className='qr-container mb-4' ref={qrRef}>
          <QRCode
            value={qrValue}
            size={256}
            style={{ height: 'auto', maxWidth: '100%', width: '100%' }}
            viewBox='0 0 256 256'
          />
        </div>

        <div className='qr-description text-sm text-gray-500 text-center'>
          {t('mgmt.qrModal.scanHelp')}
        </div>
      </div>
    </ModalComponent>
  )
}

export default QRModal
